import { List, Show, Create, Edit, Datagrid, TextField, DateField, NumberField, SimpleForm, TextInput, SelectInput, NumberInput, DateInput, Button, Filter, SearchInput, useListContext, useRecordContext, ChipField } from 'react-admin';
import { Box, Typography, Paper, Chip, Stepper, Step, StepLabel, Alert, Grid, IconButton, Divider, AlertTitle } from '@mui/material';
import { Receipt, CheckCircle, Warning, Schedule, Cancel, PictureAsPdf, Close, Send } from '@mui/icons-material';
import { PieChart } from '@mui/x-charts-pro';
import { Link, useNavigate } from 'react-router-dom';
import type { ChipColor } from '@/types/chip';
import { ListPageHeader } from '@/components/ListPageHeader';
import type { InvoiceResponse, ReceiptResponse } from '@rosta/data-model/application-agnostic/ledger/dtos';

const INVOICE_STATUS = ['Draft', 'Issued', 'Paid', 'Overdue', 'Cancelled', 'Credited'] as const;
const RECEIPT_STATUS = ['Issued', 'Allocated', 'Cancelled', 'Refunded'] as const;
const PAYMENT_METHODS = ['EFT', 'Cash', 'Cheque', 'DebitOrder', 'CreditCard', 'Other'] as const;
type InvoiceStatusValue = (typeof INVOICE_STATUS)[number];
type ReceiptStatusValue = (typeof RECEIPT_STATUS)[number];

const INVOICE_STATUS_COLORS: Record<InvoiceStatusValue, ChipColor> = {
  Draft: 'default',
  Issued: 'info',
  Paid: 'success',
  Overdue: 'error',
  Cancelled: 'default',
  Credited: 'warning',
};

const RECEIPT_STATUS_COLORS: Record<ReceiptStatusValue, ChipColor> = {
  Issued: 'info',
  Allocated: 'success',
  Cancelled: 'default',
  Refunded: 'warning',
};

const INVOICE_STATUS_FLOW: InvoiceStatusValue[] = ['Issued', 'Paid'];

const formatCurrency = (amount: number | undefined, currency: string = 'ZAR'): string => {
  if (amount === undefined || amount === null) return '—';
  return `${currency} ${amount.toLocaleString()}`;
};

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB');
};

// Invoice Components

export const InvoiceList = () => (
  <List filters={<InvoiceFilters />}>
    <InvoiceListHeader />
    <Datagrid rowClick="show">
      <TextField source="invoiceNumber" />
      <TextField source="description" />
      <NumberField source="total" options={{ style: 'currency', currency: 'ZAR' }} />
      <TextField source="currency" />
      <ChipField source="status" />
      <DateField source="issueDate" />
      <DateField source="dueDate" />
    </Datagrid>
  </List>
);

const InvoiceListHeader = () => {
  const { data: invoices } = useListContext();
  const typed = invoices as unknown as InvoiceResponse[] | undefined;
  const total = typed?.length ?? 0;
  const paid = typed?.filter((i) => i.status === 'Paid').length ?? 0;
  const overdue = typed?.filter((i) => i.status === 'Overdue').length ?? 0;
  const issued = typed?.filter((i) => i.status === 'Issued').length ?? 0;
  const cancelled = typed?.filter((i) => i.status === 'Cancelled').length ?? 0;
  const credited = typed?.filter((i) => i.status === 'Credited').length ?? 0;

  const totalValue = typed?.reduce((sum, i) => sum + (i.total ?? 0), 0) ?? 0;

  return (
    <ListPageHeader
      title="Invoices"
      metrics={[
        { label: 'Total Invoices', value: String(total), color: 'primary.main' },
        { label: 'Total Value', value: `R ${(totalValue / 1000000).toFixed(1)}M`, color: 'info.main' },
        { label: 'Paid', value: String(paid), color: 'success.main' },
        { label: 'Overdue', value: String(overdue), color: 'error.main' },
      ]}
      pieChartData={[
        { label: 'Paid', value: paid },
        { label: 'Overdue', value: overdue },
        { label: 'Issued', value: issued },
        { label: 'Cancelled', value: cancelled },
        { label: 'Credited', value: credited },
      ].filter((d) => d.value > 0)}
      pieChartTitle="Invoice Status"
    />
  );
};

export const InvoiceFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search invoice #" alwaysOn />
    <SelectInput source="status" choices={INVOICE_STATUS.map((s) => ({ id: s, name: s }))} />
  </Filter>
);

const InvoiceShowContent = () => {
  const record = useRecordContext<InvoiceResponse>();
  const navigate = useNavigate();
  if (!record) return null;

  const isOverdue =
    record.status === 'Overdue' ||
    (record.status === 'Issued' && new Date(record.dueDate) < new Date());
  const isPaid = record.status === 'Paid';
  const isCancelled = record.status === 'Cancelled';
  const isCredited = record.status === 'Credited';
  const activeStep = INVOICE_STATUS_FLOW.indexOf(
    record.status === 'Overdue' ? 'Issued' : (record.status as InvoiceStatusValue)
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/invoices')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>

      {isOverdue && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
          <AlertTitle>Invoice Overdue</AlertTitle>
          Payment required immediately — past due date {formatDate(record.dueDate)}
        </Alert>
      )}
      {isPaid && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          <AlertTitle>Invoice Paid</AlertTitle>
          Paid on {formatDate(record.paidDate)}
        </Alert>
      )}
      {isCancelled && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Cancel />}>
          <AlertTitle>Invoice Cancelled</AlertTitle>
          This invoice has been cancelled and is no longer payable.
        </Alert>
      )}
      {isCredited && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<Receipt />}>
          <AlertTitle>Invoice Credited</AlertTitle>
          A credit note has been issued against this invoice.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>{record.invoiceNumber}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={record.status} size="small" color={INVOICE_STATUS_COLORS[record.status]} />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h6" color="primary.main" sx={{ mt: 0.5 }}>
              {formatCurrency(record.total, record.currency)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Paid Amount</Typography>
            <Typography variant="h6" color="success.main" sx={{ mt: 0.5 }}>
              {formatCurrency(record.paidAmount, record.currency)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Issue Date</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{formatDate(record.issueDate)}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Due Date</Typography>
            <Typography variant="body1" sx={{ mt: 0.5, color: isOverdue ? 'error.main' : 'inherit' }}>
              {formatDate(record.dueDate)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Description</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.description ?? '—'}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Line Items</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.lineItems?.length ?? 0}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {record.lineItems && record.lineItems.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Line Items</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box component="th" sx={{ textAlign: 'left', p: 1 }}>Description</Box>
                  <Box component="th" sx={{ textAlign: 'right', p: 1 }}>Qty</Box>
                  <Box component="th" sx={{ textAlign: 'right', p: 1 }}>Unit Price</Box>
                  <Box component="th" sx={{ textAlign: 'right', p: 1 }}>Total</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {record.lineItems.map((item, idx) => (
                  <Box component="tr" key={idx} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box component="td" sx={{ p: 1 }}>{item.description}</Box>
                    <Box component="td" sx={{ p: 1, textAlign: 'right' }}>{item.quantity}</Box>
                    <Box component="td" sx={{ p: 1, textAlign: 'right' }}>{formatCurrency(item.unitPrice, record.currency)}</Box>
                    <Box component="td" sx={{ p: 1, textAlign: 'right' }}>{formatCurrency(item.total, record.currency)}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Invoice Lifecycle</Typography>
            <Stepper activeStep={activeStep >= 0 ? activeStep : 0} alternativeLabel>
              <Step completed={activeStep > 0}>
                <StepLabel>Issued</StepLabel>
              </Step>
              <Step completed={record.status === 'Paid'}>
                <StepLabel>Paid</StepLabel>
              </Step>
            </Stepper>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Quick Actions</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button component={Link} to="/receipts" variant="outlined" size="small" startIcon={<Receipt />}>
                View Receipts
              </Button>
              <Button variant="outlined" size="small" startIcon={<PictureAsPdf />}>
                Download PDF
              </Button>
              <Button variant="outlined" size="small" startIcon={<Send />}>
                Send Reminder
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export const InvoiceShow = () => (
  <Show>
    <InvoiceShowContent />
  </Show>
);

export const InvoiceCreate = () => (
  <Create>
    <SimpleForm>
      <TextInput source="invoiceNumber" fullWidth required />
      <TextInput source="description" fullWidth multiline />
      <DateInput source="issueDate" required />
      <DateInput source="dueDate" required />
      <SelectInput source="status" choices={INVOICE_STATUS.map((s) => ({ id: s, name: s }))} defaultValue="Issued" />
    </SimpleForm>
  </Create>
);

export const InvoiceEdit = () => (
  <Edit>
    <SimpleForm>
      <TextInput source="invoiceNumber" fullWidth />
      <TextInput source="description" fullWidth multiline />
      <DateInput source="issueDate" />
      <DateInput source="dueDate" />
      <DateInput source="paidDate" />
      <NumberInput source="total" />
      <NumberInput source="paidAmount" />
      <SelectInput source="status" choices={INVOICE_STATUS.map((s) => ({ id: s, name: s }))} />
    </SimpleForm>
  </Edit>
);

// Receipt Components

export const ReceiptList = () => (
  <List filters={<ReceiptFilters />}>
    <Datagrid rowClick="show">
      <TextField source="receiptNumber" />
      <TextField source="paymentMethod" label="Method" />
      <NumberField source="amount" options={{ style: 'currency', currency: 'ZAR' }} />
      <TextField source="currency" />
      <ChipField source="status" />
      <DateField source="paymentDate" />
    </Datagrid>
  </List>
);

export const ReceiptFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search receipt #" alwaysOn />
    <SelectInput source="paymentMethod" choices={PAYMENT_METHODS.map((m) => ({ id: m, name: m }))} />
    <SelectInput source="status" choices={RECEIPT_STATUS.map((s) => ({ id: s, name: s }))} />
  </Filter>
);

const ReceiptShowContent = () => {
  const record = useRecordContext<ReceiptResponse>();
  const navigate = useNavigate();
  if (!record) return null;

  const allocatedInvoiceIds = record.allocatedInvoices?.map((a) => a.invoiceId) ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/receipts')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>

      {record.status === 'Allocated' && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          <AlertTitle>Receipt Allocated</AlertTitle>
          Payment allocated to {allocatedInvoiceIds.length} invoice(s)
        </Alert>
      )}
      {record.status === 'Cancelled' && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Cancel />}>
          <AlertTitle>Receipt Cancelled</AlertTitle>
          This receipt has been cancelled and is no longer valid
        </Alert>
      )}
      {record.status === 'Refunded' && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<Receipt />}>
          <AlertTitle>Receipt Refunded</AlertTitle>
          This receipt has been refunded
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Receipt Number</Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.5 }}>{record.receiptNumber}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={record.status} size="small" color={RECEIPT_STATUS_COLORS[record.status]} />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Amount</Typography>
            <Typography variant="h6" color="success.main" sx={{ mt: 0.5 }}>
              {formatCurrency(record.amount, record.currency)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Payment Method</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.paymentMethod}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Receipt Date</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{formatDate(record.receiptDate)}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Allocated Date</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{formatDate(record.allocatedDate)}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Description</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.description ?? '—'}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Reference</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.reference ?? '—'}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {allocatedInvoiceIds.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Allocated Invoices</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {allocatedInvoiceIds.map((id) => (
              <Chip key={id} label={id} size="small" component={Link} to={`/invoices/${id}/show`} clickable />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export const ReceiptShow = () => (
  <Show>
    <ReceiptShowContent />
  </Show>
);

export const ReceiptCreate = () => (
  <Create>
    <SimpleForm>
      <TextInput source="receiptNumber" fullWidth required />
      <SelectInput source="paymentMethod" choices={PAYMENT_METHODS.map((m) => ({ id: m, name: m }))} required />
      <NumberInput source="amount" required />
      <TextInput source="currency" defaultValue="ZAR" />
      <DateInput source="receiptDate" required />
      <TextInput source="description" fullWidth multiline />
      <TextInput source="reference" fullWidth />
      <SelectInput source="status" choices={RECEIPT_STATUS.map((s) => ({ id: s, name: s }))} defaultValue="Issued" />
    </SimpleForm>
  </Create>
);

export const ReceiptEdit = () => (
  <Edit>
    <SimpleForm>
      <TextInput source="receiptNumber" fullWidth />
      <SelectInput source="paymentMethod" choices={PAYMENT_METHODS.map((m) => ({ id: m, name: m }))} />
      <NumberInput source="amount" />
      <TextInput source="currency" />
      <DateInput source="receiptDate" />
      <DateInput source="allocatedDate" />
      <TextInput source="description" fullWidth multiline />
      <TextInput source="reference" fullWidth />
      <SelectInput source="status" choices={RECEIPT_STATUS.map((s) => ({ id: s, name: s }))} />
    </SimpleForm>
  </Edit>
);
